import { useCallback, useRef, useState } from 'react';
import { ms } from '@arc/shared/util-js';
export const DEFAULT_EXPANDED_LOADING_HEIGHT = '300px';
export const animateExpandRecommendedStyle = {
  overflow: 'hidden',
  transition: 'height 0.5s'
};

/**
 * The height to expand to if the height of the content could not be computed.
 *
 * This should never actually get used, but it makes things more robust.
 */
const fallbackExpandedHeight = 'auto';
const fallbackErrorMessage = `
You may have noticed that your expanded element
suddenly jumped from 0 to full height. This is
because \`useAnimateExpand\` was unable to complete
its full transition lifecycle, so a fallback was
triggered to ensure that the element remained visible.
The likely reason is that your component did not render
completely on initialization. Common causes include
the loading of missing translation files (i.e. calling
\`t('users:something')\` without including 'users'
in \`useTranslation\`).
`.replaceAll('\n', ' ').trim();

/**
 * When the container has finished expanding, set its height to `auto`
 * so that it will resize if the content changes.
 */
const transitionEndHeight = 'auto';
function preferredHeight(element) {
  const calcHeight = element.offsetHeight - element.clientHeight + element.scrollHeight +
  // This plus one ensures that there is a difference between initial and updated height
  // of the elements. If they are the same, a transition will not occur thus the
  // onTransitionEnd callback will not be run
  1;
  if (calcHeight === 1 && process.env.NODE_ENV !== 'production') {
    console.warn(fallbackErrorMessage);
  }
  return calcHeight === 1 ? fallbackExpandedHeight : calcHeight;
}
/**
 * Smoothly animate an element's height between a collapsed state and an expanded state.
 *
 * @deprecated Use AnimateExpand instead
 */
export function useAnimateExpand(options) {
  const {
    collapsedHeight,
    initialCollapsed = true,
    onExpandComplete
  } = options;
  const containerRef = useRef(null);
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [height, setHeight] = useState(initialCollapsed ? collapsedHeight : fallbackExpandedHeight);
  const expand = useCallback(() => {
    setCollapsed(false);

    // Wait for a moment so that the content can change and then set
    // the container's height so that it will expand to fit the content.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setHeight(containerRef.current ? preferredHeight(containerRef.current) : fallbackExpandedHeight);
      });
    });
    // Need to set height to auto so it will fit dynamically added content
    setTimeout(() => {
      setHeight(fallbackExpandedHeight);
      onExpandComplete?.();
    }, 1 * ms.seconds);
  }, [onExpandComplete]);
  const collapse = useCallback(() => {
    // Transitions do not work with dynamic values such as `auto`.
    // Set the container's style height to its current height in px
    // to make sure that the transition will work.
    setHeight(containerRef.current?.offsetHeight ?? fallbackExpandedHeight);
    setCollapsed(true);

    // Wait for the height style change to register.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Now set the container's style height so that it starts collapsing.
        setHeight(collapsedHeight);
      });
    });
  }, [collapsedHeight]);
  const onTransitionEnd = useCallback(event => {
    if (event.propertyName === 'height' && !collapsed) {
      setHeight(transitionEndHeight);
    }
  }, [collapsed]);
  return {
    collapsed,
    collapse,
    expand,
    containerProps: {
      ref: containerRef,
      style: {
        ...animateExpandRecommendedStyle,
        height
      },
      onTransitionEnd
    }
  };
}